import * as Sentry from '@sentry/react';
import { createHashHistory } from 'history';
import React from 'react';
import { Redirect, Route, Router, Switch, matchPath } from 'react-router-dom';

export const history = createHashHistory();

import { EventStreamProvider } from '../features/event-stream';
import { Navbar } from '../features/navbar';
import WhiteLabel from '../filters/WhiteLabel';
import capitalizeFilter from '../filters/capitalize-filter';
import '../utils/featureToggles';
import { enrichAxiosEvent } from '../utils/sentryAxios';
import GlobalStyle from './GlobalStyle';

import { CurriculumProvider } from '../contexts/CurriculumContext';
import { AnalyticsProvider, AnalyticsRouteListener } from '../features/analytics';
import { UserProvider } from '../features/authentication';
import { OrgProvider } from '../features/org';

import AccountProfile from '../components/AccountProfile';

import BibleStudies from '../features/curriculum/BibleStudies';
import SubscribeBibleStudy from '../features/curriculum/SubscribeBibleStudy';

import BibleStudySession from '../features/curriculum/BibleStudySession';
import BibleStudySessionMaterial from '../features/curriculum/BibleStudySessionMaterial';
import BibleStudySessionTimeline from '../features/curriculum/BibleStudySessionTimeline';
import BibleStudySessionTimelinePreview from '../features/curriculum/BibleStudySessionTimelinePreview';

import HelpAndFeedback from '../components/HelpAndFeedback';
import { FeedbackViewer } from '../features/feedbackViewer';
import FeedbackAssessmentViewer from '../features/steps/FeedbackAssessmentViewer';

import ManageCurriculum from '../features/curriculum/builder/ManageCurriculum';
import ManageCurriculumCollection from '../features/curriculum/builder/ManageCurriculumCollection';
import ManageCurriculumMaterial from '../features/curriculum/builder/ManageCurriculumMaterial';
import ManageCurriculumSession from '../features/curriculum/builder/ManageCurriculumSession';
import ManageCurriculumUnit from '../features/curriculum/builder/ManageCurriculumUnit';
import AddVolumeHeaderMaterial from '../features/curriculum/volume/AddVolumeHeaderMaterial';
import ManageCurriculumVolume from '../features/curriculum/volume/ManageCurriculumVolume';
import ManageCurriculumVolumeHeader from '../features/curriculum/volume/ManageCurriculumVolumeHeader';

import PairLanding from '../features/pairing/PairLanding';
import RecentPurchases from '../features/pairing/RecentPurchases';
import SelectOrg from '../features/pairing/SelectOrg';
import { BrowserSupport, ContactInitiative } from '../features/support';

import { CreateOrg, OrganizationOverview, OrganizationSettings, SwitchOrg } from '../features/org';

import { ManageRole, RoleAddPeople, Roles } from '../features/roles';

import {
  AddPeopleToLicense,
  ManageLicenses,
  ManageSubscriptions,
  SwitchCurriculum,
} from '../features/manageSubscriptions';

import { AssessmentResults, AssessmentResultsViewerRouter } from '../features/assessment';
import {
  AddTrainingToButton,
  AddTrainingToCarousel,
  AddTrainingToCompilation,
  TrainingCatalog,
  TrainingCompilation,
} from '../features/catalog';
import { ChurchCares } from '../features/churchCares';
import ManageCurriculums from '../features/curriculum/builder/ManageCurriculums';
import ManageSession from '../features/curriculum/timeline/ManageSession';
import ManageSessionTimeline from '../features/curriculum/timeline/ManageSessionTimeline';
import SessionCalendar from '../features/curriculum/timeline/SessionCalendar';
import { Onboarding } from '../features/curriculumOnboarding';
import Group from '../features/groups/Group';
import GroupAddPeople from '../features/groups/GroupAddPeople';
import {
  AcceptEasyLink,
  AcceptInvite,
  AddGroupsToInvite,
  AddRolesToInvite,
  AddTrainingToInvite,
  InviteSettings,
  ManageEmailInvite,
  RedeemInvite,
  ViewInvite,
} from '../features/invites';
import AdvancedInvites from '../features/invites/AdvancedInvites';
import AdvancedInvitesEasyLink from '../features/invites/AdvancedInvitesEasyLink';
import ManageAssignments from '../features/manageAssignments/ManageAssignments';
import ManageAssignmentsViewAssignment from '../features/manageAssignments/ManageAssignmentsViewAssignment';
import {
  AddTrainingCompilation,
  ManageCarouselItem,
  ManageTrainingButton,
  ManageTrainingCatalog,
  ManageTrainingCompilation,
  ManageTrainingCompilations,
} from '../features/manageCatalog';
import { AddPeople, ManagePeople } from '../features/people';
import EmailGroup from '../features/people/EmailGroup';
import EmailPerson from '../features/people/EmailPerson';
import Person from '../features/people/Person';
import RemovePeopleFromOrg from '../features/people/RemovePeopleFromOrg';
import {
  AddToYourTraining,
  AssessmentBuilder,
  AssessmentQuestionView,
  AssessmentResultsFilter,
  AssessmentResultsViewer as AssessmentResultsViewerV1,
  AssessmentSettings,
  RequireOrgForTraining,
  TaskViewerRedirect,
  Training,
  TrainingViewer,
} from '../features/training';
import AssignTraining from '../features/training/AssignTraining';
import TrainingProgress from '../features/trainingProgress/TrainingProgress';

import {
  CourseBuilder,
  ManagePathwayEmail,
  ManageTraining,
  ManageVideoContent,
  PathwayBuilder,
  PathwaySettings,
  SelectCourse,
} from '../features/manageTraining';
import CourseSettings from '../features/training/CourseSettings';

import PurchaseRedirectHandler from '../components/PurchaseRedirectHandler';
import { AdminController, ImpersonationBanner } from '../features/admin';
import { Login, Logout, OAuth, SilentRedirect } from '../features/authentication';
import { ChurchAccountCreation, ConnectChurchAccount } from '../features/churchAccount';
import { SelectOrgForChurchCares } from '../features/churchCares';
import {
  AddPeopleToCollection,
  CurriculumOverview,
  ManageBibleStudyAdvancePlanning,
  ManageBibleStudyCollection,
  ManageBibleStudyPeople,
  ManageBibleStudyResetSchedule,
  ManageBibleStudySchedule,
  ManageBibleStudySession,
  ManageBibleStudySessionTimeline,
} from '../features/curriculumAdmin';
import { Dashboard } from '../features/dashboard';
import PendingInvites from '../features/invites/PendingInvites';
import {
  ErrorBoundary,
  ErrorBoundaryCheck,
  PageNotFound,
  ServiceUnavailable,
  UnauthorizedAccount,
} from '../features/status';
import Steps from '../features/steps/Steps';
import SearchTraining from '../features/training/SearchTraining';
import { AlertToastHost } from '../services/AlertService';
import { permissions, RouteGuard } from './RouteGuard';
import { UnpairedLicenseCheck } from '../features/access';
/**
 * path: the route after `/#`
 * title: used for document.title
 * component: rendered for the path; receives route props
 * withNavbar: false for fullscreen pages
 * isPublic: allow access when no authenticated user exists
 * requiredPermission: enforce a single user permission
 * requireAnyPermission: enforce any-of permissions in the provided list
 * requiredPermissionByBuildType: for routes with a `buildType` param, enforce build for build routes and author+catalog for author routes
 * tab: optional; omit when no primary nav tab should be highlighted
 */
const routeDefinitions = [
  { path: '/', title: 'Login', component: Login, withNavbar: false, isPublic: true },
  { path: '/login', title: 'Login', component: Login, withNavbar: false, isPublic: true },
  { path: '/oauth2/:path', title: 'Login', component: OAuth, withNavbar: false, isPublic: true },
  {
    path: '/silent-redirect',
    title: 'Silent Redirect',
    component: SilentRedirect,
    withNavbar: false,
    isPublic: true,
  },
  { path: '/logout', title: 'Logout', component: Logout, withNavbar: false, isPublic: true },
  {
    path: '/task-viewer/:courseId/:taskId?/:workflowId?',
    title: 'Task Viewer',
    component: TaskViewerRedirect,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/create/:fromLogin?',
    title: 'Create',
    component: () => <Redirect to="/org/create" />,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/training-pathway/:pathwayId',
    title: 'Training Pathway',
    component: () => <Redirect to="/dashboard" />,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/impersonate',
    title: 'Impersonate',
    component: () => <Redirect to="/admin/impersonate" />,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/accept-invite/:orgId/:forEmail',
    title: 'Accept Invite',
    component: ({ match }) => <Redirect to={`/invite/accept/${match.params.orgId}/${match.params.forEmail}`} />,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/purchaseCheck',
    title: 'Purchase Check',
    component: () => <Redirect to="/pairing/checkout" />,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/onboarding/churchcares/church-select/:trainingId',
    title: 'Select Org and Assign',
    component: SelectOrgForChurchCares,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/onboarding/:onboardingName/:action?',
    title: 'Onboarding',
    component: ChurchCares,
    withNavbar: false,
    isPublic: true,
  },
  { path: '/catalog', title: 'Catalog', component: TrainingCatalog, isPublic: true, tab: 'Catalog' },
  {
    path: '/catalog/compilation/:compilationId',
    title: 'Catalog',
    component: TrainingCompilation,
    isPublic: true,
    tab: 'Catalog',
  },
  {
    path: '/catalog/compilation/:compilationId/:trainingId',
    title: 'Catalog',
    component: Training,
    isPublic: true,
    tab: 'Catalog',
  },
  { path: '/people', title: 'People', component: ManagePeople, requiredPermission: permissions.invite, tab: 'People' },
  {
    path: '/people/add',
    title: 'Add People',
    component: AddPeople,
    requiredPermission: permissions.invite,
    tab: 'People',
  },
  {
    path: '/groups',
    title: 'People',
    component: ManagePeople,
    requireAnyPermission: [permissions.groups, permissions.groupLeader],
    tab: 'People',
  },
  {
    path: '/groups/:groupId/:orgId',
    title: 'People',
    component: Group,
    requireAnyPermission: [permissions.groups, permissions.groupLeader],
    tab: 'People',
  },
  {
    path: '/groups/:groupId/:orgId/add-people',
    title: 'People',
    component: GroupAddPeople,
    requireAnyPermission: [permissions.groups, permissions.groupLeader],
    tab: 'People',
  },
  { path: '/roles', title: 'Roles', component: Roles, requiredPermission: permissions.invite, tab: 'Organization' },
  {
    path: '/roles/:roleId',
    title: 'Roles',
    component: ManageRole,
    requiredPermission: permissions.invite,
    tab: 'Organization',
  },
  {
    path: '/roles/:roleId/add-people',
    title: 'Roles',
    component: RoleAddPeople,
    requiredPermission: permissions.invite,
    tab: 'Organization',
  },

  {
    path: '/org',
    title: 'Organization',
    component: OrganizationOverview,
    requireAnyPermission: [
      permissions.org,
      permissions.invite,
      permissions.assign,
      permissions.build,
      permissions.groups,
      permissions.author,
      permissions.curriculum,
    ],
    tab: 'Organization',
  },

  {
    path: '/assign-training/:trainingId',
    title: 'Catalog',
    component: AssignTraining,
    requireAnyPermission: [permissions.assign, permissions.groupLeader],
  },

  {
    path: '/people/remove',
    title: 'Remove People',
    component: RemovePeopleFromOrg,
    requiredPermission: permissions.org,
    tab: 'People',
  },

  { path: '/account-profile', title: 'Account Profile', component: AccountProfile, tab: 'Profile' },
  { path: '/bible-studies', title: 'Curriculum', component: BibleStudies, isPublic: true, tab: 'Curriculum' },
  {
    path: '/subscribe-bible-study/:brandCode/:curriculumId',
    title: 'Curriculum',
    component: SubscribeBibleStudy,
    isPublic: true,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/overview/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'Curriculum Overview',
    component: CurriculumOverview,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/people/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'People',
    component: ManageBibleStudyPeople,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/people/collection/:crmBrandCode/:crmAgeCategory/:curriculumId/:collectionId',
    title: 'Collection',
    component: ManageBibleStudyCollection,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/people/collection/add-people/:crmBrandCode/:crmAgeCategory/:curriculumId/:collectionId',
    title: 'Add People',
    component: AddPeopleToCollection,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/schedule/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'Schedule',
    component: ManageBibleStudySchedule,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/schedule/:crmBrandCode/:crmAgeCategory/:curriculumId/:sessionId',
    title: 'Session',
    component: ManageBibleStudySession,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/schedule/:crmBrandCode/:crmAgeCategory/:curriculumId/:sessionId/timeline/:originalTimelineId/:timelineId',
    title: 'Session Timeline',
    component: ManageBibleStudySessionTimeline,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/advance-planning/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'Advance Planning',
    component: ManageBibleStudyAdvancePlanning,
    tab: 'Curriculum',
  },
  {
    path: '/manage-bible-study/reset-schedule/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'Reset Schedule',
    component: ManageBibleStudyResetSchedule,
    tab: 'Curriculum',
  },

  { path: '/help', title: 'Help And Feedback', component: HelpAndFeedback, tab: 'Help' },

  { path: '/pairing/purchases', title: 'Recent Purchases', component: RecentPurchases, withNavbar: false },
  {
    path: '/pairing/select-org',
    title: `Select A ${capitalizeFilter(WhiteLabel.labels.org)}`,
    component: SelectOrg,
    withNavbar: false,
  },
  { path: '/pairing/:checkout?', title: 'Purchase Pairing', component: PairLanding, withNavbar: false },

  {
    path: '/org/create',
    title: `Create A ${capitalizeFilter(WhiteLabel.labels.org)}`,
    component: window.featureToggles.get('AOM_ORG_CREATION') === true ? ChurchAccountCreation : CreateOrg,
    withNavbar: false,
  },
  {
    path: '/org/connect',
    title: `Connect A ${capitalizeFilter(WhiteLabel.labels.org)}`,
    component: ConnectChurchAccount,
    withNavbar: false,
  },
  {
    path: '/org/switch',
    title: `Select A ${capitalizeFilter(WhiteLabel.labels.org)}`,
    component: SwitchOrg,
    withNavbar: false,
  },
  {
    path: '/org/settings',
    title: 'Organization Settings',
    component: OrganizationSettings,
    requiredPermission: permissions.org,
    tab: 'Organization',
  },
  {
    path: '/org/purchases-and-subscriptions',
    title: 'Purchases and Subscriptions',
    component: ManageSubscriptions,
    requiredPermission: permissions.org,
    tab: 'Organization',
  },
  {
    path: '/org/purchases-and-subscriptions/:licenseId',
    title: 'Licenses',
    component: ManageLicenses,
    requiredPermission: permissions.org,
    tab: 'Organization',
  },
  {
    path: '/org/purchases-and-subscriptions/:subscriptionId/switch',
    title: 'Licenses',
    component: SwitchCurriculum,
    requiredPermission: permissions.org,
    tab: 'Organization',
  },
  {
    path: '/org/purchases-and-subscriptions/:licenseId/add',
    title: 'Licenses',
    component: AddPeopleToLicense,
    tab: 'Organization',
    requiredPermission: permissions.org,
  },

  { path: '/person/:id', title: 'Organization', component: Person, tab: 'People' },
  {
    path: '/catalog/compilation/:compilationId/:trainingId',
    title: 'Training',
    component: Training,
    tab: 'Catalog',
  },
  {
    path: '/manage-catalog',
    title: 'Manage Training Catalog',
    component: ManageTrainingCatalog,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/feature-carousel/:carouselIndex?',
    title: 'Manage Training Catalog',
    component: ManageTrainingCatalog,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/carousel-item/:atIndex/:id?/add-training',
    title: 'Manage Training Catalog',
    component: AddTrainingToCarousel,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/training-button/:buttonType/:id?/add-training',
    title: 'Manage Training Catalog',
    component: AddTrainingToButton,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/carousel-item/:atIndex/:id?',
    title: 'Manage Training Catalog',
    component: ManageCarouselItem,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/training-button/:buttonType/:id?',
    title: 'Manage Training Catalog',
    component: ManageTrainingButton,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/compilations',
    title: 'Manage Training Catalog',
    component: ManageTrainingCompilations,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/compilation/:id?/add-training',
    title: 'Manage Training Catalog',
    component: AddTrainingToCompilation,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/compilation/:id?',
    title: 'Manage Training Catalog',
    component: ManageTrainingCompilation,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/add-compilation/:index',
    title: 'Manage Training Catalog',
    component: AddTrainingCompilation,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/add-compilation-to-carousel-item',
    title: 'Manage Training Catalog',
    component: AddTrainingCompilation,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },
  {
    path: '/manage-catalog/add-compilation-to-training-button',
    title: 'Manage Training Catalog',
    component: AddTrainingCompilation,
    requiredPermission: permissions.author,
    tab: 'Organization',
  },

  {
    path: '/manage-assignments',
    title: 'Organization',
    component: ManageAssignments,
    requireAnyPermission: [permissions.assign, permissions.groupLeader],
  },

  {
    path: '/manage-assignments/:assignmentId',
    title: 'Organization',
    component: ManageAssignmentsViewAssignment,
    requireAnyPermission: [permissions.assign, permissions.groupLeader],
  },
  {
    path: '/manage-assignments/:assignmentId/view/:assessmentId/:responseId',
    title: 'Assessment Results',
    component: () => <AssessmentResultsViewerRouter V1ResultsViewer={AssessmentResultsViewerV1} />,
    requireAnyPermission: [permissions.assign, permissions.groupLeader],
  },
  {
    path: '/manage-curriculum',
    title: 'Manage Curriculum',
    component: ManageCurriculums,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  // Manage Curriculum - Non-Timeline Enabled (legacy)
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId',
    title: 'Manage Curriculum',
    component: ManageCurriculum,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/unit/:unitId',
    title: 'Manage Curriculum',
    component: ManageCurriculumUnit,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/unit/:unitId/session',
    title: 'Manage Curriculum',
    component: ManageCurriculumSession,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/unit/:unitId/session/:sessionId/settings',
    title: 'Manage Curriculum',
    component: ManageCurriculumSession,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/unit/:unitId/session/:sessionId/material',
    title: 'Manage Curriculum',
    component: ManageCurriculumMaterial,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/unit/:unitId/session/:sessionId/material/:materialId/settings',
    title: 'Manage Curriculum',
    component: ManageCurriculumMaterial,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/volume',
    title: 'Manage Curriculum',
    component: ManageCurriculumVolume,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/volume/:volumeId',
    title: 'Manage Curriculum',
    component: ManageCurriculumVolume,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/volume/:volumeId/header/:headerId',
    title: 'Manage Curriculum',
    component: ManageCurriculumVolumeHeader,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/volume/:volumeId/header/:headerId/add-material',
    title: 'Manage Curriculum',
    component: AddVolumeHeaderMaterial,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/collection',
    title: 'Manage Curriculum',
    component: ManageCurriculumCollection,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/collection/:collectionId',
    title: 'Manage Curriculum',
    component: ManageCurriculumCollection,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  // Mange Curriculum - Timeline Enabled
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/session-calendar',
    title: 'Manage Curriculum',
    component: SessionCalendar,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/session/:sessionId/timeline/:timelineId',
    title: 'Manage Curriculum',
    component: ManageSessionTimeline,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  {
    path: '/manage-curriculum/:crmBrandCode/:crmAgeCategory/:curriculumId/session/:sessionId/timeline/:timelineId/edit',
    title: 'Manage Curriculum',
    component: ManageSession,
    requiredPermission: permissions.curriculum,
    tab: 'Organization',
  },
  // End Manage Curriculum Routes
  {
    path: '/curriculum/:crmBrandCode/:crmAgeCategory/session/:sessionId/issue/:issueId',
    title: 'Curriculum',
    component: BibleStudySession,
    withNavbar: false,
    tab: 'Curriculum',
  },
  {
    path: '/curriculum/:crmBrandCode/:crmAgeCategory/session/:sessionId/material/:materialId',
    title: 'Curriculum',
    component: BibleStudySessionMaterial,
    withNavbar: false,
    tab: 'Curriculum',
  },
  {
    path: '/curriculum/:crmBrandCode/:crmAgeCategory/session/:sessionId/issue/:issueId/timeline/:timelineId/preview',
    title: 'Curriculum',
    component: BibleStudySessionTimelinePreview,
    withNavbar: false,
    tab: 'Curriculum',
  },
  {
    path: '/curriculum/:crmBrandCode/:crmAgeCategory/session/:sessionId/issue/:issueId/timeline/:timelineId/:timelineCustomizationId/preview',
    title: 'Curriculum',
    component: BibleStudySessionTimelinePreview,
    withNavbar: false,
    tab: 'Curriculum',
  },
  {
    path: '/curriculum/:crmBrandCode/:crmAgeCategory/session/:sessionId/issue/:issueId/timeline/:timelineId/:timelineCustomizationId?',
    title: 'Curriculum',
    component: BibleStudySessionTimeline,
    withNavbar: false,
    tab: 'Curriculum',
  },
  {
    path: '/dashboard/:page?',
    title: 'Dashboard',
    component: Dashboard,
    tab: 'Training',
  },
  {
    path: '/search',
    title: 'Search',
    component: SearchTraining,
    tab: 'Catalog',
  },
  {
    path: '/email/person/:orgId/:personId',
    title: 'People',
    component: EmailPerson,
    tab: 'People',
  },
  {
    path: '/email/group/:orgId/:groupId',
    title: 'Organization',
    component: EmailGroup,
    tab: 'People',
  },
  {
    path: '/manage-training/:buildType/assessments/:assessmentId',
    title: 'Assessment Builder',
    component: AssessmentBuilder,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/assessments/:assessmentId/settings',
    title: 'Assessment Settings',
    component: AssessmentSettings,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/assessments/:assessmentId/questions/:questionId?',
    title: 'Assessment Question View',
    component: AssessmentQuestionView,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/curriculum/onboarding/:brandCode/:step?',
    title: 'Curriculum',
    component: Onboarding,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/assessment/results',
    title: 'Assessment Results',
    component: AssessmentResults,
    tab: 'Organization',
    requiredPermission: permissions.assign,
  },
  {
    path: '/assessment/results/:assessmentId',
    title: 'Assessment Results Viewer',
    component: () => <AssessmentResultsViewerRouter V1ResultsViewer={AssessmentResultsViewerV1} />,
    tab: 'Organization',
    requiredPermission: permissions.assign,
  },
  {
    path: '/assessment/results/:assessmentId/filter',
    title: 'Assessment Results Filter',
    component: AssessmentResultsFilter,
    tab: 'Organization',
    requiredPermission: permissions.assign,
  },
  {
    path: '/training-progress',
    title: 'Training Progress',
    component: TrainingProgress,
    tab: 'Organization',
    requiredPermission: permissions.assign,
  },
  {
    path: '/admin/*',
    title: 'Administration',
    component: AdminController,
  },
  {
    path: '/purchase',
    title: 'Purchase',
    component: PurchaseRedirectHandler,
  },
  {
    path: '/invites',
    title: 'Invites',
    component: AdvancedInvites,
    requiredPermission: permissions.invite,
    tab: 'Organization',
  },
  {
    path: '/invite/accept/:orgId/:forEmail',
    title: 'Accept Invite',
    component: AcceptInvite,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/invite/:orgId/code/view/:inviteId',
    title: 'Invites',
    component: AdvancedInvitesEasyLink,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/redeem-invite/:prefix?/:suffix?',
    title: 'Redeem Invite',
    component: RedeemInvite,
    withNavbar: false,
  },
  {
    path: '/invite/:orgId/:inviteType/add-training/:inviteId?/:settingsId?',
    title: 'Invites',
    component: AddTrainingToInvite,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/invite/:orgId/:inviteType/add-groups',
    title: 'Invites',
    component: AddGroupsToInvite,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/invite/:orgId/:inviteType/add-roles',
    title: 'Invites',
    component: AddRolesToInvite,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/invite/:orgId/:inviteType/settings/:inviteId?/:settingsId?',
    title: 'Invite Settings',
    component: InviteSettings,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/invite/:orgId/email/preview/:inviteId?',
    title: 'Invites',
    component: ManageEmailInvite,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/invite/:orgId/:inviteType/:inviteId',
    title: 'Invites',
    component: ViewInvite,
    requiredPermission: permissions.invite,
    tab: 'Invites',
  },
  {
    path: '/manage-training/:buildType/:trainingType',
    title: 'Manage Training',
    component: ManageTraining,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/pathways/:trainingId',
    title: 'Manage Training',
    component: PathwayBuilder,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/pathways/:trainingId/settings',
    title: 'Manage Training',
    component: PathwaySettings,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/:trainingType/:trainingId/select',
    title: 'Manage Training',
    component: SelectCourse,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/courses/:trainingId',
    title: 'Manage Training',
    component: CourseBuilder,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/courses/:trainingId/settings',
    title: 'Manage Training',
    component: CourseSettings,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/:trainingType/:trainingId/email/:emailId?',
    title: 'Manage Training',
    component: ManagePathwayEmail,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/manage-training/:buildType/content/:contentId',
    title: 'Manage Training',
    component: ManageVideoContent,
    requiredPermissionByBuildType: true,
    tab: 'Organization',
  },
  {
    path: '/pending-invites',
    title: 'Pending Invites',
    component: PendingInvites,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/training/:trainingId',
    title: 'Training',
    component: Training,
    isPublic: true,
    tab: 'Training',
  },
  {
    path: '/training-viewer/:type/:id',
    title: 'Training',
    component: TrainingViewer,
    withNavbar: false,
    tab: 'Training',
  },
  {
    path: '/training-steps/:trainingId',
    title: 'Training',
    component: Steps,
    withNavbar: false,
  },
  {
    path: '/feedback-assessment-taker/:courseId/:stepId/:email',
    title: 'Feedback Assessment Taker',
    component: FeedbackAssessmentViewer,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/feedback-viewer/:id/:taskListInstanceId/:isPersonal?',
    title: 'Feedback Viewer',
    component: FeedbackViewer,
  },
  {
    path: '/catalog/training/:trainingId',
    title: 'Training',
    component: Training,
    isPublic: true,
    tab: 'Catalog',
  },
  {
    path: '/training/begin/:trainingId',
    title: 'Begin Training',
    component: RequireOrgForTraining,
  },
  {
    path: '/add-to-your-training/:trainingId',
    title: 'Add to Your Training',
    component: AddToYourTraining,
    withNavbar: false,
  },
  {
    path: '/easy-link/:prefix/:groupCode?',
    title: 'Easy Link',
    component: AcceptEasyLink,
    withNavbar: false,
  },
  {
    path: '/contact/:initiative',
    title: 'Contact',
    component: ContactInitiative,
    withNavbar: false,
  },
  {
    path: '/401',
    title: 'Unauthorized',
    component: UnauthorizedAccount,
  },
  {
    path: '/503',
    title: 'Service Unavailable',
    component: ServiceUnavailable,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/browser-support',
    title: 'Browser Support',
    component: BrowserSupport,
    withNavbar: false,
    isPublic: true,
  },
  {
    path: '/error-check',
    title: 'Encountered Error',
    component: ErrorBoundaryCheck,
    isPublic: true,
  },
  {
    path: '*',
    title: 'Page Not Found',
    component: PageNotFound,
    isPublic: true,
  },
];

export const routes = routeDefinitions.map(route => ({ ...route, exact: route.path !== '*' }));

export default function ReactApp() {
  return (
    <ErrorBoundary>
      <UserProvider>
        <EventStreamProvider>
          <OrgProvider>
            <UnpairedLicenseCheck />
            <AnalyticsProvider>
              <GlobalStyle />
              <div id="alerts-banner-container" />
              <Router history={history}>
                <Switch>
                  {routes.map(route => (
                    <Route
                      exact={route.exact}
                      path={route.path}
                      key={route.path}
                      render={props => {
                        document.title = `${WhiteLabel.name} - ${route.title}`;
                        return (
                          <CurriculumProvider>
                            <AnalyticsRouteListener pageName={route.component.name} />
                            <div style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                              <ImpersonationBanner />
                              <Navbar activeTab={route.tab} hideNavbar={route.withNavbar === false} />
                              <AlertToastHost />
                            </div>
                            <RouteGuard route={route} routeProps={props}>
                              {React.createElement(route.component, props)}
                            </RouteGuard>
                          </CurriculumProvider>
                        );
                      }}
                    />
                  ))}
                </Switch>
              </Router>
            </AnalyticsProvider>
          </OrgProvider>
        </EventStreamProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

function deriveEnvironment() {
  const { hostname } = window.location;
  if (hostname.includes('local')) {
    return 'local';
  } else if (hostname.includes('dev') || hostname.includes('int')) {
    return 'integration';
  } else if (hostname.includes('stage') || hostname.includes('uat')) {
    return 'user-acceptance';
  } else {
    return 'production';
  }
}

const environment = deriveEnvironment();

Sentry.init({
  dsn: 'https://b75a765e71264d7387be8b50335e8d2d@o99218.ingest.sentry.io/4505319015448576',
  environment,
  release: window.config?.SENTRY_RELEASE,
  integrations: [Sentry.reactRouterV5BrowserTracingIntegration({ history, routes, matchPath })],
  tracesSampleRate: environment === 'local' ? 0 : 0.1,
  beforeSend: enrichAxiosEvent,
  ignoreErrors: [
    // OIDC Client Library Errors
    // TODO: Fixing these issues in the client library helps all apps that use the library. No user impact has been observed due to these issues at this time. - Jordan Fjellman 09/07/2023
    'No matching state found in storage',
    'Worker was terminated',
    'Load failed',
    // Authorization errors are not sent because they are used to determine if the user is logged in
    'Request failed with status code 403',
    // Legacy AngularJS error (kept for historical client sessions)
    '[$rootScope:inprog]',
    // Fullstory API error
    'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    // Test error
    'Error check',
  ],
});
